import React from 'react'
import {
  Grid,
  TextField,
  Typography,
  Divider,
  Box,
  Button,
  Select,
  MenuItem,
  FormControl,
  InputLabel
} from '@mui/material'
import { FormInputRow, ActionButton } from '@emerald-works-nova/components'
import { useStyles } from './style'
import { ACTIONS, getOrdinalSuffix } from '../utils'

const MAX_CARDS = 6

const FeaturesSectionForm = ({ section, onChange, isEdit }) => {
  const styles = useStyles()
  const { header, subHeader, description, cards } = section.data

  const handleFieldChange = (field, value) => {
    onChange(section.order, { ...section, data: { ...section.data, [field]: value } })
  }

  const handleFeatureCardChange = (cardOrder, field, value) => {
    const updatedCards = cards?.map(card =>
      card.order === cardOrder ? { ...card, [field]: value } : card
    ) || []
    handleFieldChange('cards', updatedCards)
  }

  const addNewCard = () => {
    const existingCards = cards || []
    const newId = Math.max(...existingCards.map(c => c.order), 0) + 1
    const newCard = {
      order: newId,
      header: `Feature ${newId}`,
      title: '',
      description: '',
      cta: '',
      action: ''
    }
    handleFieldChange('cards', [...existingCards, newCard])
  }

  const removeCard = (cardOrder) => {
    const updatedCards = cards?.filter(card => card.order !== cardOrder) || []
    handleFieldChange('cards', updatedCards)
  }

  return (
    <Grid container spacing={3} className={styles.formContainer} data-test='features-section-form-container'>
      <Grid item xs={12} data-test='features-section-form-header-container'>
        <FormInputRow
          title='Header'
          description='Main heading for the features section'
          smallFormTitles
          InputComponent={
            <TextField
              fullWidth
              label='Header'
              value={header || ''}
              onChange={(e) => handleFieldChange('header', e.target.value)}
              variant='outlined'
              disabled={!isEdit}
              data-test='features-section-form-header-input'
            />
          }
        />
      </Grid>

      <Grid item xs={12} data-test='features-section-form-subheader-container'>
        <FormInputRow
          title='Subheader'
          description='Supporting headline for the features section'
          smallFormTitles
          InputComponent={
            <TextField
              fullWidth
              label='Subheader'
              value={subHeader || ''}
              onChange={(e) => handleFieldChange('subHeader', e.target.value)}
              variant='outlined'
              disabled={!isEdit}
              data-test='features-section-form-subheader-input'
            />
          }
        />
      </Grid>

      <Grid item xs={12} data-test='features-section-form-description-container'>
        <FormInputRow
          title='Description'
          description='Detailed description for the features section'
          smallFormTitles
          InputComponent={
            <TextField
              fullWidth
              label='Description'
              value={description || ''}
              onChange={(e) => handleFieldChange('description', e.target.value)}
              variant='outlined'
              multiline
              rows={3}
              disabled={!isEdit}
              data-test='features-section-form-description-input'
            />
          }
        />
      </Grid>

      <Grid item xs={12} data-test='features-section-form-cards-container'>
        <Divider className={styles.sectionDivider} />
        <Typography variant='subtitle1' gutterBottom>
          Feature Cards
        </Typography>
        <Typography variant='body2' color='textSecondary' className={styles.sectionDescription}>
          Configure the individual feature cards that will be displayed in the features section. You can add up to a maximum of {MAX_CARDS} feature cards.
        </Typography>

        <Grid container spacing={2}>
          {cards?.map((card, index) => (
            <Grid item xs={12} key={index} data-test={`features-section-form-card-${index}-container`}>
              <Box className={styles.cardContainer}>
                <Box className={styles.cardHeader}>
                  <Typography variant='subtitle1' className={styles.cardTitle}>
                    {index + 1}{getOrdinalSuffix(index + 1)} Feature Card
                  </Typography>
                  {(cards?.length > 1) && isEdit && (
                    <ActionButton
                      variant='secondary'
                      onClick={() => removeCard(card.order)}
                      dataTest={`features-section-form-card-${index}-remove-button`}
                    >
                      Remove
                    </ActionButton>
                  )}
                </Box>

                <Grid container spacing={2}>
                  <Grid item md={12} lg={5}>
                    <Grid container direction='column' spacing={2}>
                      <Grid item>
                        <TextField
                          fullWidth
                          label='Title'
                          value={card.title || ''}
                          onChange={(e) => handleFeatureCardChange(card.order, 'title', e.target.value)}
                          variant='outlined'
                          disabled={!isEdit}
                          data-test={`features-section-form-card-${index}-title-input`}
                        />
                      </Grid>
                      <Grid item>
                        <TextField
                          fullWidth
                          label='CTA Text'
                          value={card.cta || ''}
                          onChange={(e) => handleFeatureCardChange(card.order, 'cta', e.target.value)}
                          variant='outlined'
                          disabled={!isEdit}
                          data-test={`features-section-form-card-${index}-cta-input`}
                        />
                      </Grid>
                      <Grid item>
                        <FormControl fullWidth variant='outlined' disabled={!isEdit} required>
                          <InputLabel>CTA Action</InputLabel>
                          <Select
                            value={card.action || ''}
                            onChange={(e) => handleFeatureCardChange(card.order, 'action', e.target.value)}
                            label='CTA Action'
                            data-test={`features-section-form-card-${index}-action-select`}
                          >
                            <MenuItem value=''>
                              <em>Select an action</em>
                            </MenuItem>
                            {Object.values(ACTIONS).map((action) => (
                              <MenuItem key={action.value} value={action.value}>
                                {action.label}
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </Grid>
                  </Grid>
                  <Grid item md={12} lg={7}>
                    <TextField
                      fullWidth
                      label='Description'
                      value={card.description || ''}
                      onChange={(e) => handleFeatureCardChange(card.order, 'description', e.target.value)}
                      variant='outlined'
                      multiline
                      rows={7}
                      disabled={!isEdit}
                      data-test={`features-section-form-card-${index}-description-input`}
                    />
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          ))}
        </Grid>

        {isEdit && cards && cards.length < MAX_CARDS && (
          <Box mt={3} className={styles.addCardContainer} data-test='features-section-form-add-card-container'>
            <Button
              variant='outlined'
              onClick={addNewCard}
              className={styles.addButton}
              data-test='features-section-form-add-card-button'
            >
              Add New Feature Card
            </Button>
          </Box>
        )}
      </Grid>
    </Grid>
  )
}

export default FeaturesSectionForm
